import { InsertPhoto } from "@mui/icons-material";
import CloudUploadOutlinedIcon from "@mui/icons-material/CloudUploadOutlined";
import InfoIcon from "@mui/icons-material/Info";
import OfflineBoltOutlinedIcon from "@mui/icons-material/OfflineBoltOutlined";
import StickyNote2OutlinedIcon from "@mui/icons-material/StickyNote2Outlined";
import {
  Chip,
  CircularProgress,
  Link,
  Stack,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";
import {
  useGridApiRef,
  type GridColDef,
  type GridFilterModel,
  type GridSortModel,
} from "@mui/x-data-grid-premium";
import DOMPurify from "dompurify";
import { cloneDeep, isEqual } from "lodash";
import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { PatientContext } from "../../../contexts/PatientContext";
import { ProjectContext } from "../../../contexts/ProjectContext";
import { projectsPrefix } from "../../../services/ProjectsServices.ts";
import { useFetch } from "../../../services/hooks/useFetch.ts";
import { getDiffDaysMessage } from "../../../utils/main.ts";
import BaseTable from "../../datagrid/BaseTable.tsx";
import { BaseTableToolbar } from "../../datagrid/BaseTableToolbar.tsx";
import { TableNoRowsCustomization } from "../../datagrid/TableNoRowsCustomization.tsx";
import DocumentModal from "./DocumentModal.tsx";
import { filters, remapNoteFilters } from "./filters/ESDocFilters.ts";

enum NoteType {
  CLINICAL_NOTE = "Clinical Note",
  ORDER_NARRATIVE = "Order Narrative",
  RADIOLOGY_REPORT = "Radiology Report",
  PATHOLOGY_REPORT = "Pathology Report",
}

const CustomNoRowsOverlay = () => {
  return (
    <TableNoRowsCustomization
      icon={
        <CloudUploadOutlinedIcon
          style={{
            color: "#0D3E56",
            width: "48px",
            height: "48px",
            marginLeft: "18px",
          }}
        />
      }
      text={"This visit does not include any Clinical Notes."}
    />
  );
};

export default function NotesTable({
  categorize,
  visitId,
  encounterNotesCount,
  encounterNumber,
  visitNameText,
  newNotesCount,
}: {
  categorize: (e?: React.MouseEvent<HTMLElement>) => void;
  visitId: string;
  encounterNotesCount: number;
  encounterNumber: number;
  visitNameText?: string;
  newNotesCount: number;
}) {
  const {
    id: projectId,
    highlightDate,
    radiologyImagesEnabled,
  } = useContext(ProjectContext)!;
  const theme = useTheme();
  const { id } = useContext(PatientContext)!;
  const [documentModal, setDocumentModal] = useState(false);
  const [note, setNote] = useState<ESDoc | null>(null);
  const {
    data: notesData,
    create: fetchNotes,
    isLoading: isNotesLoading,
    error: fetchNotesError,
    reset: resetNotesFetch,
  } = useFetch<PagedResponse<ESDoc>, FilterPageSortRequest>();

  const [paginationModel, setPaginationModel] = React.useState({
    pageSize: 100,
    page: 0,
  });

  const [filter, setFilter] = useState<GridFilterModel>({
    items: [],
    quickFilterExcludeHiddenColumns: false,
  });

  const [sortModel, setSortModel] = useState<GridSortModel>([
    { field: "diffDays", sort: "asc" },
  ]);

  const [previouslyUsedFetchParams, setPreviouslyUsedFetchParams] = useState<
    (FilterPageSortRequest & { visitId: string }) | null
  >(null);

  const [requireNotesFetch, setRequireNotesFetch] = useState(true);
  const [viewStateReady, setViewStateReady] = useState(false);

  const notesDataTableFields: GridColDef<ESDoc>[] = [
    {
      field: "noteTitle",
      headerName: "Title",
      width: 130,
      filterOperators: filters.noteTitle,
    },
    {
      field: "diffDays",
      headerName: "Days Pre / Post Index",
      width: 115,
      valueGetter: (value) => getDiffDaysMessage(value),
      filterOperators: filters.diffDays,
    },
    {
      field: "noteType",
      headerName: "Note Type",
      type: "singleSelect",
      valueOptions: [
        NoteType.CLINICAL_NOTE,
        NoteType.ORDER_NARRATIVE,
        NoteType.RADIOLOGY_REPORT,
        NoteType.PATHOLOGY_REPORT,
      ],
      valueGetter: (value) => {
        return NoteType[value];
      },
      filterOperators: filters.noteType,
    },
    {
      field: "notePreview",
      headerName: "Content",
      flex: 1,
      valueGetter: (_value, row) => row.notePreview,
      renderCell: (params) => {
        const processedText = params.row.notePreview.replaceAll("\n", "<br />");

        const textWithEllipsis =
          processedText.length >= 200 ? `${processedText}...` : processedText;

        const finalText = DOMPurify.sanitize(textWithEllipsis, {
          ALLOWED_TAGS: ["highlight"],
          RETURN_TRUSTED_TYPE: true,
        });

        return (
          <Stack direction={"column"} spacing={1}>
            <Link
              sx={{ textAlign: "left" }}
              component="button"
              variant="subtitle2"
              onClick={() => {
                handleDocumentModal(params.row);
              }}
              dangerouslySetInnerHTML={{ __html: finalText }}
            />
            {/*  todo: add search match count once we move to server-side search */}
            {radiologyImagesEnabled &&
              (
                params.row.radiologyImageInfo?.filter(
                  (img) => !!img.radiologyImageAvailable,
                ) ?? []
              ).length > 0 && (
                <Chip
                  label={"DICOM Images"}
                  size="small"
                  icon={<InsertPhoto />}
                  sx={{
                    width: "fit-content",
                  }}
                />
              )}
          </Stack>
        );
      },
      filterOperators: filters.notePreview,
    },
    {
      field: "keywordScore",
      headerName: "Priority",
      renderHeader: () => (
        <div className="MuiDataGrid-columnHeaderTitle">
          <Tooltip
            title="Visits with a priority icon may include evidence of an adverse event, clinical or performance failure as identified by the 3Aware system."
            placement="top-end"
            className="priority"
          >
            <p>
              Priority
              <InfoIcon
                fontSize="small"
                sx={{ color: theme.palette.info.main }}
              />
            </p>
          </Tooltip>
        </div>
      ),
      renderCell: (params) => {
        return (
          params.row.keywordScore > 0 && (
            <OfflineBoltOutlinedIcon sx={{ color: theme.palette.info.main }} />
          )
        );
      },
      align: "center",
      width: 115,
      filterOperators: filters.keywordScore,
    },
  ];

  const apiRef = useGridApiRef();

  const noteIndex = useMemo(() => {
    if (!notesData?.results || notesData.results.length === 0 || !note)
      return -1;
    return notesData.results.findIndex((n) => n.documentId === note.documentId);
  }, [note, notesData]);

  function handleDocumentModal(document: ESDoc) {
    setDocumentModal(true);
    setNote(document);
  }

  function goPrevDocument() {
    if (noteIndex > 0 && notesData?.results) {
      const prevNoteId = notesData.results[noteIndex - 1].documentId;
      setNote(
        notesData.results.find((note) => note.documentId === prevNoteId) ||
          null,
      );
    }
  }

  function goNextDocument() {
    if (notesData?.results && noteIndex < notesData.results.length - 1) {
      const nextNoteId = notesData.results[noteIndex + 1].documentId;
      setNote(
        notesData.results.find((note) => note.documentId === nextNoteId) ||
          null,
      );
    }
  }

  useEffect(() => {
    if (fetchNotesError) {
      console.log("Error: " + fetchNotesError);
    }
  }, [fetchNotesError]);

  useEffect(() => {
    resetNotesFetch();
    setRequireNotesFetch(true);
  }, [resetNotesFetch, visitId]);

  const getNotes = useCallback(() => {
    // Convert MUI filters into ES filter format
    let localFilterItems = filter.items.map((item) => {
      const filterItem = cloneDeep(item);
      const itemValue: unknown = cloneDeep(filterItem.value);
      if (typeof itemValue === "object" && Array.isArray(itemValue)) {
        filterItem.value = itemValue.reduce((acc, curr) => {
          if (typeof curr === "string") {
            if (acc !== "") {
              acc = acc + "," + curr;
            } else {
              acc = curr;
            }
          } else {
            if (acc !== "") {
              acc = acc + "," + JSON.stringify(curr);
            } else {
              acc = JSON.stringify(curr);
            }
          }
          return acc;
        }, "");
      } else if (typeof itemValue !== "string") {
        filterItem.value = JSON.stringify(itemValue);
      }

      return filterItem;
    });

    localFilterItems = localFilterItems
      ? remapNoteFilters(localFilterItems)
      : [];

    const fetchBody = {
      page: paginationModel.page,
      pageSize: paginationModel.pageSize,
      sort: [...sortModel],
      search: filter?.quickFilterValues?.join(" ").trim() || "",
      filters: localFilterItems,
      filterLogicalOperator: filter?.logicOperator?.toUpperCase(),
    };

    if (
      !isEqual(previouslyUsedFetchParams, { ...fetchBody, visitId: visitId })
    ) {
      setPreviouslyUsedFetchParams({ ...fetchBody, visitId: visitId });

      void fetchNotes({
        path: `${projectsPrefix}/${projectId}/patients/${id}/encounters/${visitId}/notes`,
        body: fetchBody,
      });
    }
  }, [
    fetchNotes,
    projectId,
    id,
    visitId,
    paginationModel.page,
    paginationModel.pageSize,
    filter?.items,
    filter?.logicOperator,
    filter?.quickFilterValues,
    previouslyUsedFetchParams,
    sortModel,
  ]);

  useEffect(() => {
    if (requireNotesFetch && viewStateReady) {
      getNotes();
      setRequireNotesFetch(false);
    }
  }, [getNotes, requireNotesFetch, viewStateReady]);

  return (
    <div className="modal-content-visits-right">
      <div className="modal-content-right-header">
        <div className="modal-content-clinical-notes">
          <StickyNote2OutlinedIcon />
          <Typography variant="body1">Clinical Notes</Typography>
          {notesData && !isNotesLoading ? (
            <>
              <Chip
                label={encounterNotesCount}
                size="small"
                sx={{
                  marginLeft: "-8px",
                }}
              />
              {!!highlightDate && (
                <Chip
                  label={(newNotesCount || 0) + " new"}
                  size="small"
                  sx={{
                    marginLeft: "-8px",
                    backgroundColor: "highlight.main",
                    textTransform: "none",
                  }}
                />
              )}
            </>
          ) : (
            <CircularProgress size={20} />
          )}
        </div>
      </div>
      <BaseTable<ESDoc>
        apiRef={apiRef}
        disableEval={true}
        boxStyling={{
          height: (notesData?.results?.length ?? 0) > 0 ? "100%" : "300px",
          width: "100%",
          alignItems: "center",
          justifyContent: "center",
        }}
        className={"note-table"}
        loading={isNotesLoading}
        slots={{
          noRowsOverlay: CustomNoRowsOverlay,
          toolbar: BaseTableToolbar,
        }}
        slotProps={{
          toolbar: {
            showColumns: false,
            showQuickFilter: true,
            quickFilterColumns: "title or content",
            quickFilterTextFieldTooltip:
              "This search uses both exact and partial matching depending on the field. If you aren't seeing expected results, try adjusting your search terms.",
            onViewStateReady: () => setViewStateReady(true),
          },
        }}
        columnVisibilityModel={{
          noteText: false,
          noteType: false,
        }}
        columnHeaderHeight={55}
        highlightField={"loadedAt"}
        columns={notesDataTableFields}
        rows={notesData?.results || []}
        disableColumnSelector
        ignoreDiacritics
        getRowId={(row) => row.documentId}
        tableId="clinical-notes-table"
        rowCount={notesData?.rowCount ?? 0}
        paginationMode="server"
        paginationModel={paginationModel}
        onPaginationModelChange={(model) => {
          setPaginationModel(model);
          setRequireNotesFetch(true);
        }}
        filterMode="server"
        filterModel={filter}
        onFilterModelChange={(filterModel) => {
          setFilter(filterModel);
          setRequireNotesFetch(true);
        }}
        sortingMode="server"
        sortModel={sortModel}
        onSortModelChange={(model) => {
          setSortModel(model);
          setRequireNotesFetch(true);
        }}
        filterDebounceMs={600}
        sx={{
          "& .MuiDataGrid-virtualScroller": {
            overflow: "hidden",
          },
        }}
      />

      {documentModal && note && (
        <DocumentModal
          document={note}
          categorize={categorize}
          closeModal={() => setDocumentModal(false)}
          visitInfo={{
            encounterNumber,
            visitNameText,
            visitId,
          }}
          isFirst={noteIndex === 0}
          isLast={
            !!notesData?.results && noteIndex === notesData.results.length - 1
          }
          goPrevDocument={goPrevDocument}
          goNextDocument={goNextDocument}
        />
      )}
    </div>
  );
}
